import { Injectable } from '@angular/core';
import { ValidatorFn, FormGroup, ValidationErrors, FormGroupDirective, AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MESSAGE_TYPE } from 'src/app/constant/constant';
import { SnackbarService } from './snackbar.service';
import { AUTH } from 'src/app/constant/constant';

/**
 * @author ToiTV
 * 2020-10-30
 */

@Injectable({
    providedIn: 'root'
})
export class ValidationService {
    constructor(private message: SnackbarService,
        private translate: TranslateService) { }

    trimSpace(event, formControl) {
        if (event.target.value != null && event.target.value != '') {
          formControl.patchValue(event.target.value.toString().trim());
        }
      }
    /**
     *
     * @param value
     */
     static resetForm(form: FormGroup, formDirective?: FormGroupDirective): void {
        if (formDirective) formDirective.resetForm();
        form.reset();
    }

    /**
     * Check Only
     * @param str
     * @param characters
     */
    static isOnly(str: string, characters: { toString(): string }): boolean {
        const chars = characters
            .toString()
            .replace(/\\/g, '\\\\')
            .replace(/\(/g, '\\(')
            .replace(/\)/g, '\\)')
            .replace(/\[/g, '\\[')
            .replace(/\]/g, '\\]');
        return new RegExp('^[' + chars + ']+$', 'gm').test(str);
    }

    /**
     * Check null and ""
     * @param obj
     */
    static isBlank(obj: any): boolean {
        return obj === undefined || obj === null || (typeof obj === "string" && obj == "");
    }

    /**
     * Check String type
     * @param obj
     */
    static isString(obj: any): boolean {
        return typeof obj === "string";
    }

    /**
     * Has value
     * @param value
     */
    static hasValue(value): boolean {
        if (ValidationService.isBlank(value) ||
            (ValidationService.isString(value) && value.trim() === '')) {
            return false;
        }

        return true;
    }

    static emailValidator(control) {
        // RFC 2822 compliant regex
        if (control.value.match(/[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/)) {
            return null;
        } else {
            return { 'invalidEmailAddress': true };
        }

    }

    static checkEmailValidator(email: String): boolean {
        if (email == null) {
            return false;
        }
        if (this.isDoubleByte(email.toString())) {
            return false;
        }
        var regex = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
        return regex.test(email.toString());
    }

    static passwordValidator(pass): boolean {
        // {8,100}           - Assert password is between 8 and 100 characters
        // (?=.*[0-9])       - Assert a string has at least one number
        if (/^(?=.*[0-9])[a-zA-Z0-9!@#$%^&*]{8,100}$/.test(pass)) {
            return true;
        } else {
            return false;
        }
    }

    /**
    * Method to check double byte
    * @param str
    */
    static isDoubleByte(str: string): boolean {
        return /[^\u0000-\u00ff]/.test(str);
    }
    /**
    * Method to check Range of Value Integer
    * @param numberCheck
    */
    static checkRangeInteger(numberCheck: number): boolean {
        let minRange: number = -2147483648;
        let maxRange: number = 2147483647;
        if (numberCheck >= minRange && numberCheck <= maxRange) {
            return true;
        }
        else {
            return false;
        }
    }

    /**
    * Method to check Range of Value Money
    * @param numberCheck
    */
   static checkRangeMoney(numberCheck: number): boolean {
    let minRange: number = 0;
    let maxRange: number = 2147483647;
    if (numberCheck >= minRange && numberCheck <= maxRange) {
        return true;
    }
    else {
        return false;
    }
}

    /**
    * Method to check valid number
    * @param str  Input value
    * @return result: true if input value is number, else return false; msg: message error
    */
    static isValidNumber(str: any): { result: boolean } {
        if (str == null || str.toString().trim() == "") {
            return { result: false };
        }

        if (this.isDoubleByte(str)) {
            return { result: false };
        }

        var keyPattern = /^[0-9\-]*$/;
        if (!keyPattern.test(str)) {
            return { result: false };
        }

        return { result: true };
    }

    /**
    * Method to check phone number
    * @param str 
    * @return result: true if input value is phonex format, else return false; msg: message error
    */
    static isPhone(str: any): { result: boolean } {
        // Validate number
        var result = this.isValidNumber(str);

        // Validate phone format
        if (result.result) {
            var keyPattern = /^\d{10}$/;
            return { result: keyPattern.test(str) };
        }
        return result;
    }

    /**
    * Method to check digit is number
    * @param digit
    */
    static isNumberDigit(digit: string): boolean {
        var keyPattern = /^[0-9]$/;
        return keyPattern.test(digit);
    }
    /**
     * Method to check is number
     * @param value
     */
    static isNumber(value: string): boolean {
        var keyPattern = /^[0-9]*$/;
        return keyPattern.test(value);
    }

    /**
     * Method to check is CMND number
     * @param value
     */
    static isCMNDNumber(value: string): boolean {
        return (this.isNumber(value) && (value.length == 9 || value.length == 12));
    }

    static comparisonValidator(validations): ValidatorFn {
        return (group: FormGroup): ValidationErrors => {
            // Độ ưu tiên 2
            // Check ragne Integer
            if (validations.int) {
                validations.int.forEach(p => {
                    var control = group.controls[p.id];
                    if (!this.isBlank(control.value) && !this.checkRangeMoney(control.value)) {
                        control.setErrors(p);
                    } else {
                        control.setErrors(null);
                    }
                })
            }

            if (validations.integer) {
                validations.integer.forEach(p => {
                    var control = group.controls[p.id];
                    if (!this.isBlank(control.value) && (!this.checkRangeMoney(control.value) || !/^[0-9]+$/.test(control.value))) {
                        control.setErrors(p);
                    } else {
                        control.setErrors(null);
                    }
                })
            }


            // Độ ưu tiên 2
            // Check range year
            if(validations.year) {
                validations.year.forEach(p => {
                    var control = group.controls[p.id];
                    if (!this.isBlank(control.value) && (control.value < 1900 || control.value > new Date().getFullYear())) {
                        control.setErrors(p);
                    } else {
                        control.setErrors(null);
                    }
                })
            }

            // Độ ưu tiên 2
            // Check CMND number
            if(validations.cmnd) {
                validations.cmnd.forEach(p => {
                    var control = group.controls[p.id];
                    if (!this.isBlank(control.value) && !this.isCMNDNumber(control.value)) {
                        control.setErrors(p);
                    } else {
                        control.setErrors(null);
                    }
                })
            }

                        // Độ ưu tiên 2
            // Check ragne motnth
            if (validations.month) {
                validations.month.forEach(p => {
                    var control = group.controls[p.id];
                    if (!this.isBlank(control.value) && (!this.checkRangeMonth(control.value) || !/^[0-9]+$/.test(control.value))) {
                        control.setErrors(p);
                    } else {    
                        control.setErrors(null);
                    }
                })
            }

            // Độ ưu tiên 1
            // Check require
            if (validations.require) {
                validations.require.forEach(p => {
                    var control = group.controls[p.id];
                    if (this.isBlank(control.value)) {
                        control.setErrors(p);
                    } else {
                        control.setErrors(null);

                        if (validations.int && validations.int.length > 0) {
                            var ints = validations.int.filter(x => x.id == p.id);
                            if (ints.length > 0) {
                                if (!this.isBlank(control.value) && !this.checkRangeMoney(control.value)) {
                                    control.setErrors(ints[0]);
                                }
                            }
                        }

                        if(validations.year && validations.year.length > 0) {
                            var years = validations.year.filter(x => x.id == p.id);
                            if (years.length > 0) {
                                if (!this.isBlank(control.value) && (control.value < 1900 || control.value > new Date().getFullYear())) {
                                    control.setErrors(years[0]);
                                }
                            }
                        }

                        if(validations.cmnd && validations.cmnd.length > 0) {
                            var cmnds = validations.cmnd.filter(x => x.id == p.id);
                            if (cmnds.length > 0) {
                                if (!this.isBlank(control.value) && (control.value < 1900 || control.value > new Date().getFullYear())) {
                                    control.setErrors(cmnds[0]);
                                }
                            }
                        }

                        if(validations.month && validations.month.length > 0) {
                            var months = validations.month.filter(x => x.id == p.id);
                            if (months.length > 0) {
                                if (!this.isBlank(control.value) && control.value >=0 && control.value <=99) {
                                    control.setErrors(months[0]);
                                }
                            }
                        }
                    }
                })
            }
            return;
        };
    }

    /**
     * STG Trí 24/12/2022
     * Validate 2 date
     */
    static compareDateValidator(keyDateBefore, keyDateAfter, errorString){
      return (group: AbstractControl): ValidationErrors | null => {
        var DateBefore = group.get(keyDateBefore);
        var DateAfter = group.get(keyDateAfter);

        if (!(DateBefore.value && DateAfter.value)){
          return null;
        }

        var DateBeforeValue, DateAfterValue;
        // Check manual dd/mm/yyyy string input
        if((typeof DateBefore.value === 'string' || DateBefore.value instanceof String) && DateBefore.value.indexOf('-') == -1){
          if(DateBefore.value.length === 4) // Year only string
            DateBeforeValue = new Date("01/01/" + DateBefore.value);
          else{
            var [day, month, year] = DateBefore.value.split("/");
            if(day.length === 4){
              year = [day, day = year][0]
            }
            DateBeforeValue = new Date(+year, +month - 1, +day);
          }

        }
        else{ // Date input
          DateBeforeValue = new Date(DateBefore?.value);
        }

        if((typeof DateAfter.value === 'string' || DateAfter.value instanceof String) && DateAfter.value.indexOf('-') == -1){
          if(DateAfter.value.length === 4) // Year only string
            DateAfterValue = new Date("12/31/" + DateAfter.value);
          else{
              var [day, month, year] = DateAfter.value.split("/");
              DateAfterValue = new Date(+year, +month - 1, +day);
          }

        }
        else{
          DateAfterValue = new Date(DateAfter?.value);
        }

        if (DateBeforeValue.getTime() - DateAfterValue.getTime() > 0) {
          var errorObj = {};
          errorObj[errorString] = true;
          DateBefore.setErrors(errorObj);
          DateAfter.setErrors(errorObj);
          return errorObj;
        } else {
          if (DateBefore.hasError(errorString)) {
            delete DateBefore.errors[errorString];
            DateBefore.updateValueAndValidity();
          }
          if (DateAfter.hasError(errorString)) {
            delete DateAfter.errors[errorString];
            DateAfter.updateValueAndValidity();
          }
          return null;
        }
      }
    }

    xoaDau(str) {
        str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
        str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
        str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
        str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
        str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
        str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
        str = str.replace(/đ/g, "d");
        str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, "A");
        str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, "E");
        str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, "I");
        str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, "O");
        str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, "U");
        str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, "Y");
        str = str.replace(/Đ/g, "D");
        return str;
    }

    validateFileAttachment(arrInputs, _validFileExtensions: any[]) {
        const DEFAULT_MB = 10;
        let maxSizeMb = DEFAULT_MB;
        const user = JSON.parse(localStorage.getItem(AUTH.USER_INFO_KEY));
        for (let i = 0; i < user.phanQuyenChucDanh.length; i++) {
            let ActionId = user.phanQuyenChucDanh[i].ActionId
            let check = user.phanQuyenChucDanh[i].Authority;
            if (ActionId.startsWith('009') && check == true) {
                const mb = parseInt(ActionId.substring(3), 10);
                if (!isNaN(mb)) {
                    if( mb > maxSizeMb ) {
                        maxSizeMb = Math.max(maxSizeMb, mb);
                    }
                }
            }
        }
        const maxSize = maxSizeMb * 1024 * 1024;

        for (var i = 0; i < arrInputs.length; i++) {
          var oInput = arrInputs[i];
          // console.log(oInput)
          var sFileName = (oInput.name).toLowerCase();
          if (sFileName.length > 0) {
            if (oInput.size > maxSize) {
              this.message.showMessage(this.translate.instant("File đính kèm dung lượng quá mức cho phép " + maxSizeMb + "M"), MESSAGE_TYPE.WARNING);
              return false;
            }
            var extension = sFileName.split('.').pop();
            if (_validFileExtensions.indexOf(extension) == -1) {
              let _str = '';
              _validFileExtensions.forEach(element => { _str = _str + element + ', ' })
              this.message.showMessage(this.translate.instant("File đính kèm không đúng định dạng " + _str.substring(0, _str.length - 2)), MESSAGE_TYPE.WARNING);
              return false;
            }
          }
        }
        return true;
      }

    static checkRangeMonth(numberCheck: number): boolean {
        let minRange: number = 0;
        let maxRange: number = 99;
        if (numberCheck >= minRange && numberCheck <= maxRange) {
            return true;
        }
        else {
            return false;
        }
    }

    checkFields(form: FormGroup, fields: { fieldName: string, errorType: string, errorMessage: string }[]): string {
        let errorMessage = '';
        fields.forEach(({ fieldName, errorType, errorMessage }) => {
            const control = form.controls[fieldName];
            if (control && control.hasError(errorType)) {
                errorMessage += `${errorMessage} ${control.getError(errorType)}<br>`;
            }
        });
        return errorMessage;
    }

    static isNotBlank(obj: any): boolean {
        return !this.isBlank(obj);
    }
}
